import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import axios from "axios";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong. Please try again.</div>;
    }
    return this.props.children;
  }
}

const ExcelUpload = ({ onFillForm }) => {
  const [file, setFile] = useState(null);
  const [itemData, setItemData] = useState([]);
  const [warehouseData, setWarehouseData] = useState([]);

  // Fetch data from APIs when the component mounts
  useEffect(() => {
    const token = localStorage.getItem("token");

    // Fetch item data
    axios
      .get(
        buildApiUrl("/api/categories"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        if (response.data && Array.isArray(response.data)) {
          setItemData(response.data);
        } else {
          toast.error("Invalid item data format.");
        }
      })
      .catch((error) => {
        toast.error("Failed to fetch item data.");
        console.error(error);
      });

    // Fetch warehouse data
    axios
      .get(
        buildApiUrl("/api/warehouse/get-warehouse/name"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          setWarehouseData(response.data.data);
        } else {
          toast.error("Invalid warehouse data format.");
        }
      })
      .catch((error) => {
        toast.error("Failed to fetch warehouse data.");
        console.error(error);
      });
  }, []);

  // Handle Excel file upload and parsing
  const handleFileUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setFile(selectedFile);
      readExcelFile(selectedFile);
    } else {
      toast.error("Please upload a valid Excel file.");
    }
  };

  // Read and parse the uploaded Excel file
  const readExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const binaryString = e.target.result;
      const workbook = XLSX.read(binaryString, { type: "binary" });

      // Assuming the first sheet has the required data
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);

      if (data.length > 0) {
        // Log the raw data for debugging
        console.log("Raw Excel Data:", data);

        // Validate the data before passing it to onFillForm
        const validatedData = data.map((row) => {
          const itemQtyCtn = row.itemQtyCtn || row.QtyCtn || ""; // Handle both possible column names
          return {
            warehouseName: row.warehouseName || "",
            itemCode: row.itemCode || "",
            itemName: row.itemName || "",
            itemCategory: row.itemCategory || "", // Ensure this is mapped correctly
            itemQtyCtn: itemQtyCtn, // Use the correct column name
            itemImage: row.itemImage || "",
            openingStock: row.openingStock || "",
            inwardReference: row.inwardReference || "",
            inwardShipmentMark: row.inwardShipmentMark || "",
          };
        });

        // Log the validated data for debugging
        console.log("Validated Data:", validatedData);

        if (typeof onFillForm === "function") {
          onFillForm(validatedData);
        } else {
          toast.error("onFillForm is not a valid function.");
        }
      } else {
        toast.error("The Excel file is empty or not in the correct format.");
      }
    };
    reader.readAsBinaryString(file);
  };

  // Generate a sample Excel file with predefined data
  const generateSampleExcel = () => {
    if (
      !itemData ||
      itemData.length === 0 ||
      !warehouseData ||
      warehouseData.length === 0
    ) {
      toast.error("Unable to generate sample Excel: Missing data.");
      return;
    }

    // Create the sample data
    const sampleData = itemData.map((item) => {
      const randomWarehouse =
        warehouseData[Math.floor(Math.random() * warehouseData.length)];
      return {
        warehouseName: randomWarehouse.warehouseName,
        itemCode: item.itemCode,
        itemName: item.itemName,
        itemCategory: item.categoryName,
        itemQtyCtn: item.pcsPerCtn,
        itemImage: item.itemImage,
        openingStock: generateRandomQuantity(),
        inwardReference: "Ref01",
        inwardShipmentMark: "NA",
      };
    });

    // Create a worksheet from the sample data
    const ws = XLSX.utils.json_to_sheet(sampleData);

    // Set custom column widths
    const columnWidths = [
      { wch: 20 }, // Warehouse Name
      { wch: 15 }, // Item Code
      { wch: 30 }, // Item Name
      { wch: 20 }, // Item Category
      { wch: 40 }, // Item Image
      { wch: 10 }, // Quantity
    ];
    ws["!cols"] = columnWidths;

    // Create a workbook and append the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample Data");

    // Save the generated Excel file
    const excelFile = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelFile], { type: "application/octet-stream" }),
      "sample_inventory.xlsx"
    );
  };

  // Function to generate random quantity
  const generateRandomQuantity = () => Math.floor(Math.random() * 100) + 1;

  return (
    <div className="excel-upload-container text-xs">
      <input type="file" accept=".xlsx" onChange={handleFileUpload} />
      <button
        type="button"
        onClick={generateSampleExcel}
        className="px-3 py-2 bg-green-700 text-white rounded-md"
      >
        Format
      </button>
    </div>
  );
};

export default ExcelUpload;
